import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.service_ticket_api';

const ENDPOINTS = {
  createTicket: `${BASE}.create_ticket`,
  getTickets: `${BASE}.get_tickets`,
  getTicket: `${BASE}.get_ticket`,
  updateTicket: `${BASE}.update_ticket`,
  submitTicket: `${BASE}.submit_ticket`,
  billTicket: `${BASE}.bill_ticket`,
  cancelTicket: `${BASE}.cancel_ticket`,
  getPendingTickets: `${BASE}.get_pending_tickets`,
};

const extract = (res) =>
  res.data?.message && typeof res.data.message === 'object'
    ? res.data.message
    : res.data?.message || res.data;

const extractErr = (err) => {
  const d = err.response?.data;
  if (d?._server_messages) {
    try {
      const msgs = JSON.parse(d._server_messages);
      const first = Array.isArray(msgs) ? msgs[0] : null;
      if (first) {
        const p = typeof first === 'string' ? JSON.parse(first) : first;
        if (p?.message) return String(p.message).replace(/<[^>]*>/g, '').trim();
      }
    } catch { /* fall through */ }
  }
  return d?.message || err.message || 'An error occurred';
};

export const createTicket = createAsyncThunk('serviceTicket/create', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.createTicket, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Ticket created', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getTickets = createAsyncThunk('serviceTicket/list', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getTickets, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getTicket = createAsyncThunk('serviceTicket/get', async (ticketId, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getTicket, { params: { ticket_id: ticketId } });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const updateTicket = createAsyncThunk('serviceTicket/update', async ({ ticketId, ...data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.updateTicket, { ticket_id: ticketId, ...data });
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Ticket updated', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const submitTicket = createAsyncThunk('serviceTicket/submit', async (ticketId, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.submitTicket, { ticket_id: ticketId });
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Ticket submitted', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const billTicket = createAsyncThunk('serviceTicket/bill', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.billTicket, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Ticket billed', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const cancelTicket = createAsyncThunk('serviceTicket/cancel', async ({ ticketId, reason }, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.cancelTicket, { ticket_id: ticketId, reason });
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Ticket cancelled', type: 'info' }));
    return { ticketId };
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getPendingTickets = createAsyncThunk('serviceTicket/pending', async (_, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getPendingTickets);
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

const serviceTicketSlice = createSlice({
  name: 'serviceTicket',
  initialState: {
    tickets: [],
    pendingTickets: [],
    currentTicket: null,
    loading: false,
    error: null,
    total: 0,
  },
  reducers: {
    clearCurrentTicket: (state) => { state.currentTicket = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(getTickets.pending, loading)
      .addCase(getTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload?.tickets || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(getTickets.rejected, failed)
      .addCase(getTicket.pending, loading)
      .addCase(getTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTicket = action.payload?.ticket || null;
      })
      .addCase(getTicket.rejected, failed)
      .addCase(createTicket.pending, loading)
      .addCase(createTicket.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.ticket) state.tickets.unshift(action.payload.ticket);
      })
      .addCase(createTicket.rejected, failed)
      .addCase(submitTicket.fulfilled, (state, action) => {
        const t = action.payload?.ticket;
        if (t) {
          const idx = state.tickets.findIndex(x => x.name === t.name);
          if (idx >= 0) state.tickets[idx] = t;
          if (state.currentTicket?.name === t.name) state.currentTicket = t;
        }
      })
      .addCase(billTicket.fulfilled, (state, action) => {
        const t = action.payload?.ticket;
        if (t) {
          const idx = state.tickets.findIndex(x => x.name === t.name);
          if (idx >= 0) state.tickets[idx] = t;
          if (state.currentTicket?.name === t.name) state.currentTicket = t;
          state.pendingTickets = state.pendingTickets.filter(x => x.name !== t.name);
        }
      })
      .addCase(cancelTicket.fulfilled, (state, action) => {
        const id = action.payload?.ticketId;
        if (id) {
          state.tickets = state.tickets.filter(x => x.name !== id);
          state.pendingTickets = state.pendingTickets.filter(x => x.name !== id);
        }
      })
      .addCase(getPendingTickets.fulfilled, (state, action) => {
        state.pendingTickets = action.payload?.tickets || [];
      });
  },
});

export const { clearCurrentTicket, clearError } = serviceTicketSlice.actions;
export default serviceTicketSlice.reducer;
